/**
 * SecurityIncidentsPage - "Incidents de sécurité"
 * Liste et gestion des incidents générés depuis le Journal d'audit & sécurité.
 * Boucle détection -> investigation -> résolution.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldAlert, AlertTriangle, Loader2, CheckCircle2, Inbox, Search, RefreshCw,
  X, User, Clock, ArrowRight, FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  open: { label: 'Ouvert', cls: 'bg-red-50 text-red-700 border-red-200' },
  investigating: { label: 'En investigation', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved: { label: 'Résolu', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed: { label: 'Clôturé', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const SEVERITY_META = {
  basse: { label: 'Basse', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  moyenne: { label: 'Moyenne', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  haute: { label: 'Haute', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  critique: { label: 'Critique', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).replace('+00:00Z', 'Z'));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Pill = ({ meta }) => (
  <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
);

const StatCard = ({ icon: Icon, title, value, accent, valueClass = 'text-gray-900', testid }) => (
  <div data-testid={testid} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className={`text-2xl font-bold mt-2 ${valueClass}`}>{value}</div>
  </div>
);

const SecurityIncidentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ status: 'all', severity: 'all', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState(null);
  const [savingNote, setSavingNote] = useState('');

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '100' });
      if (filters.status !== 'all') p.append('status', filters.status);
      if (filters.severity !== 'all') p.append('severity', filters.severity);
      if (filters.search) p.append('search', filters.search);
      const res = await fetch(`${API}/logs/incidents?${p}`, {});
      const data = await res.json();
      setIncidents(data.incidents || []);
      setStats(data.stats || {});
    } catch {
      toast({ title: 'Erreur', description: 'Chargement des incidents impossible', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const updateIncident = async (incidentId, patch) => {
    try {
      const res = await fetch(`${API}/logs/incidents/${incidentId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      const doc = await res.json();
      setSelected(doc);
      setIncidents((list) => list.map((i) => (i.incident_id === incidentId ? doc : i)));
      fetchIncidents();
      toast({ title: 'Mis à jour', description: `Incident ${incidentId}` });
      return doc;
    } catch {
      toast({ title: 'Erreur', description: 'Mise à jour impossible', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px] mx-auto" data-testid="security-incidents-page">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-orange-500" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">Incidents de sécurité</h1>
          </div>
          <p className="text-sm text-gray-600 mt-1">Suivez et résolvez les incidents détectés depuis le journal d'audit.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="btn-goto-audit" onClick={() => navigate('/admin/audit-logs')}>
            <FileText className="w-4 h-4 mr-1.5" />Journal d'audit
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-refresh" onClick={fetchIncidents}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Actualiser
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-total" icon={Inbox} title="Total incidents" value={stats.total || 0} accent="text-gray-400" />
        <StatCard testid="stat-open" icon={AlertTriangle} title="Ouverts" value={stats.open || 0} accent="text-red-500" valueClass="text-red-600" />
        <StatCard testid="stat-investigating" icon={Loader2} title="En investigation" value={stats.investigating || 0} accent="text-amber-500" valueClass="text-amber-600" />
        <StatCard testid="stat-resolved" icon={CheckCircle2} title="Résolus" value={(stats.resolved || 0) + (stats.closed || 0)} accent="text-emerald-500" valueClass="text-emerald-600" />
        <StatCard testid="stat-critical" icon={ShieldAlert} title="Sévérité haute" value={stats.critical || 0} accent="text-orange-500" valueClass="text-orange-600" />
      </div>

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setFilters((f) => ({ ...f, search: searchInput.trim() }))}
            placeholder="Rechercher référence, titre, assigné…" className="pl-9" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="w-[170px]" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.severity} onValueChange={(v) => setFilters((f) => ({ ...f, severity: v }))}>
          <SelectTrigger className="w-[160px]" data-testid="filter-severity"><SelectValue placeholder="Sévérité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sévérités</SelectItem>
            {Object.entries(SEVERITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Référence', 'Titre', 'Sévérité', 'Statut', 'Assigné à', 'Créé par', 'Date', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100"><td colSpan={8} className="px-4 py-3"><Skeleton className="h-8 w-full" /></td></tr>
                ))
              ) : incidents.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center" data-testid="empty-state">
                  <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Aucun incident de sécurité</p>
                  <p className="text-gray-500 text-sm">Les incidents créés depuis le journal d'audit apparaîtront ici.</p>
                </td></tr>
              ) : (
                incidents.map((inc) => (
                  <tr key={inc.incident_id} data-testid="incident-row" onClick={() => setSelected(inc)}
                    className="border-b border-gray-100 hover:bg-gray-50/70 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{inc.incident_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-[260px] truncate">{inc.title}</td>
                    <td className="px-4 py-3"><Pill meta={SEVERITY_META[inc.severity] || SEVERITY_META.moyenne} /></td>
                    <td className="px-4 py-3"><Pill meta={STATUS_META[inc.status] || STATUS_META.open} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{inc.assignee || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{inc.created_by || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(inc.created_at)}</td>
                    <td className="px-4 py-3 text-right"><ArrowRight className="w-4 h-4 text-gray-400 inline" /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DRAWER */}
      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto p-0" data-testid="incident-drawer">
          {selected && (
            <div className="flex flex-col h-full">
              <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-lg font-semibold text-gray-900">{selected.incident_id}</SheetTitle>
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="drawer-close" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button>
                </div>
                <p className="text-sm text-gray-700 mt-2">{selected.title}</p>
                <SheetDescription className="sr-only">Détail et gestion de l'incident de sécurité {selected.incident_id}</SheetDescription>
                <div className="flex gap-2 mt-3">
                  <Pill meta={SEVERITY_META[selected.severity] || SEVERITY_META.moyenne} />
                  <Pill meta={STATUS_META[selected.status] || STATUS_META.open} />
                </div>
              </div>

              <div className="px-6 py-5 space-y-6 flex-1">
                {/* Gestion */}
                <section className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Statut</label>
                    <Select value={selected.status} onValueChange={(v) => updateIncident(selected.incident_id, { status: v })}>
                      <SelectTrigger className="mt-1" data-testid="drawer-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Sévérité</label>
                    <Select value={selected.severity} onValueChange={(v) => updateIncident(selected.incident_id, { severity: v })}>
                      <SelectTrigger className="mt-1" data-testid="drawer-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(SEVERITY_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </section>

                <section>
                  <label className="text-xs text-gray-500">Assigné à</label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="drawer-assignee" defaultValue={selected.assignee || ''} placeholder="email@baobab.ci"
                      onBlur={(e) => { if (e.target.value !== (selected.assignee || '')) updateIncident(selected.incident_id, { assignee: e.target.value }); }} />
                  </div>
                </section>

                {/* Détails / source */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h3>
                  <div className="bg-gray-50 border border-gray-100 p-4 rounded-lg text-sm text-gray-700">{selected.description || '—'}</div>
                </section>

                {selected.snapshot && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Événement source</h3>
                    <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                      <div><div className="text-xs text-gray-500">Action</div><div className="text-gray-900">{selected.snapshot.action || '—'}</div></div>
                      <div><div className="text-xs text-gray-500">Module</div><div className="text-gray-900">{selected.snapshot.module || '—'}</div></div>
                      <div><div className="text-xs text-gray-500">Utilisateur</div><div className="text-gray-900 break-all">{selected.snapshot.user || '—'}</div></div>
                      <div><div className="text-xs text-gray-500">IP</div><div className="font-mono text-gray-900">{selected.snapshot.ip || '—'}</div></div>
                      <div className="col-span-2"><div className="text-xs text-gray-500">Event ID</div><div className="font-mono text-xs text-gray-700 break-all">{selected.source_event_id}</div></div>
                    </div>
                  </section>
                )}

                {/* Note */}
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Ajouter une note</h3>
                  <div className="flex gap-2">
                    <Input data-testid="drawer-note-input" value={savingNote} onChange={(e) => setSavingNote(e.target.value)} placeholder="Note d'investigation…" />
                    <Button size="sm" data-testid="drawer-note-add" disabled={!savingNote.trim()}
                      onClick={async () => { await updateIncident(selected.incident_id, { note: savingNote.trim() }); setSavingNote(''); }}>Ajouter</Button>
                  </div>
                </section>

                {/* Timeline */}
                {Array.isArray(selected.timeline) && selected.timeline.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Historique</h3>
                    <ul className="space-y-2" data-testid="incident-timeline">
                      {selected.timeline.slice().reverse().map((t, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Clock className="w-3.5 h-3.5 text-gray-400 mt-1 shrink-0" />
                          <div>
                            <span className="text-gray-900">
                              {t.type === 'status' && `Statut → ${(STATUS_META[t.value] || {}).label || t.value}`}
                              {t.type === 'severity' && `Sévérité → ${(SEVERITY_META[t.value] || {}).label || t.value}`}
                              {t.type === 'assignee' && `Assigné à ${t.value || '—'}`}
                              {t.type === 'note' && t.value}
                            </span>
                            <div className="text-xs text-gray-500 flex items-center gap-1"><User className="w-3 h-3" />{t.by} · {formatDate(t.at)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SecurityIncidentsPage;
